import { LitElement, html, css } from 'lit';
import { designSystem, componentStyles } from '../../styles/design-system.js';
import { iconStyles } from '../../styles/icon-styles.js';
import { campaignsStyles } from '../../styles/campaigns-styles.js';

export class VoiceCampaigns extends LitElement {
  static properties = {
    campaigns: { type: Array },
    selectedCampaign: { type: Object },
    activeTab: { type: String }
  };

  static styles = [
    designSystem,
    componentStyles,
    campaignsStyles
  ];

  constructor() {
    super();
    this.activeTab = 'results';
    this.selectedCampaign = null;
    this.campaigns = [
      {
        id: 1,
        name: 'Q1 Appointment Reminders',
        status: 'active',
        agent: 'Appointment Setter',
        createdAt: '2026-01-15',
        totalCalls: 500,
        completedCalls: 234,
        failedCalls: 12,
        voicemailCalls: 45,
        results: [
          { phone: '+1 (555) 234-5678', status: 'completed', duration: '2:34', outcome: 'Confirmed', sentiment: 'positive', time: '10:23 AM' },
          { phone: '+1 (555) 345-6789', status: 'voicemail', duration: '0:31', outcome: 'Left Message', sentiment: 'neutral', time: '10:25 AM' },
          { phone: '+1 (555) 456-7890', status: 'completed', duration: '3:12', outcome: 'Rescheduled', sentiment: 'positive', time: '10:28 AM' },
          { phone: '+1 (555) 567-8901', status: 'failed', duration: '0:00', outcome: 'No Answer', sentiment: 'neutral', time: '10:30 AM' },
          { phone: '+1 (555) 678-9012', status: 'completed', duration: '1:45', outcome: 'Confirmed', sentiment: 'positive', time: '10:33 AM' },
          { phone: '+1 (555) 789-0123', status: 'completed', duration: '4:02', outcome: 'Cancelled', sentiment: 'negative', time: '10:36 AM' },
          { phone: '+1 (555) 890-1234', status: 'voicemail', duration: '0:28', outcome: 'Left Message', sentiment: 'neutral', time: '10:38 AM' },
          { phone: '+1 (555) 901-2345', status: 'completed', duration: '2:15', outcome: 'Confirmed', sentiment: 'positive', time: '10:41 AM' }
        ],
        settings: {
          schedule: 'Weekdays 9:00 AM - 5:00 PM EST',
          maxRetries: 3,
          retryDelay: '30 minutes',
          concurrency: 5,
          callerId: '+1 (555) 100-0001',
          voicemailDetection: true,
          maxCallDuration: '5 minutes'
        },
        contacts: { total: 500, valid: 487, invalid: 13 }
      },
      {
        id: 2,
        name: 'Holiday Promo Outreach',
        status: 'completed',
        agent: 'Sales Outbound',
        createdAt: '2025-12-01',
        totalCalls: 1200,
        completedCalls: 1200,
        failedCalls: 89,
        voicemailCalls: 312,
        results: [
          { phone: '+1 (555) 111-2233', status: 'completed', duration: '3:45', outcome: 'Interested', sentiment: 'positive', time: '9:05 AM' },
          { phone: '+1 (555) 222-3344', status: 'completed', duration: '1:20', outcome: 'Not Interested', sentiment: 'negative', time: '9:08 AM' },
          { phone: '+1 (555) 333-4455', status: 'voicemail', duration: '0:30', outcome: 'Left Message', sentiment: 'neutral', time: '9:10 AM' },
          { phone: '+1 (555) 444-5566', status: 'completed', duration: '2:55', outcome: 'Follow Up', sentiment: 'positive', time: '9:14 AM' },
          { phone: '+1 (555) 555-6677', status: 'failed', duration: '0:00', outcome: 'Invalid Number', sentiment: 'neutral', time: '9:16 AM' }
        ],
        settings: {
          schedule: 'Daily 10:00 AM - 8:00 PM EST',
          maxRetries: 2,
          retryDelay: '1 hour',
          concurrency: 10,
          callerId: '+1 (555) 100-0002',
          voicemailDetection: true,
          maxCallDuration: '8 minutes'
        },
        contacts: { total: 1200, valid: 1156, invalid: 44 }
      },
      {
        id: 3,
        name: 'New Customer Welcome Calls',
        status: 'draft',
        agent: 'Welcome Agent',
        createdAt: '2026-02-08',
        totalCalls: 300,
        completedCalls: 0,
        failedCalls: 0,
        voicemailCalls: 0,
        results: [],
        settings: {
          schedule: 'Weekdays 10:00 AM - 4:00 PM EST',
          maxRetries: 2,
          retryDelay: '2 hours',
          concurrency: 3,
          callerId: '+1 (555) 100-0003',
          voicemailDetection: true,
          maxCallDuration: '6 minutes'
        },
        contacts: { total: 300, valid: 298, invalid: 2 }
      }
    ];
  }

  _getStatusBadge(status) {
    const map = {
      active: { cls: 'badge-success', label: 'Active', dot: true },
      completed: { cls: 'badge-neutral', label: 'Completed', dot: false },
      paused: { cls: 'badge-warning', label: 'Paused', dot: false },
      draft: { cls: 'badge-info', label: 'Draft', dot: false }
    };
    const info = map[status] || map.draft;
    return html`
      <span class="badge ${info.cls}">
        ${info.dot ? html`<span class="pulse-dot"></span>` : ''}
        ${info.label}
      </span>
    `;
  }

  _getProgressPercent(c) {
    if (c.totalCalls === 0) return 0;
    return Math.round((c.completedCalls / c.totalCalls) * 100);
  }

  _getRemainingCalls(c) {
    return c.totalCalls - c.completedCalls - c.failedCalls - c.voicemailCalls;
  }

  _selectCampaign(c) {
    this.selectedCampaign = { ...c };
    this.activeTab = 'results';
  }

  _backToList() {
    this.selectedCampaign = null;
  }

  _setTab(tab) {
    this.activeTab = tab;
  }

  // ─── List View ───
  _renderCampaignList() {
    return html`
      <div class="page-header">
        <div class="page-header-left">
          <h1 class="page-title">Campaigns</h1>
          <p class="page-subtitle">Manage and monitor your outbound call campaigns</p>
        </div>
        <button class="btn btn-primary" @click=${() => {}}>
          <i class="fa-sharp fa-light fa-plus"></i>
          Create Campaign
        </button>
      </div>

      <div class="campaigns-grid">
        ${this.campaigns.map(c => this._renderCampaignCard(c))}
      </div>
    `;
  }

  _renderCampaignCard(c) {
    const pct = this._getProgressPercent(c);
    const remaining = this._getRemainingCalls(c);
    return html`
      <div class="card campaign-card">
        <!-- Header -->
        <div class="campaign-card-header">
          <span class="campaign-name">${c.name}</span>
          ${this._getStatusBadge(c.status)}
        </div>

        <!-- Progress -->
        <div class="progress-section">
          <div class="progress-bar-track">
            <div
              class="progress-bar-fill ${c.status === 'completed' ? 'complete' : ''}"
              style="width: ${pct}%"
            ></div>
          </div>
          <span class="progress-count">${c.completedCalls} / ${c.totalCalls} calls</span>
        </div>

        <!-- Stats -->
        <div class="stats-row">
          <div class="mini-stat">
            <i class="fa-sharp fa-light fa-circle-check mini-stat-icon success"></i>
            <div class="mini-stat-info">
              <span class="mini-stat-value">${c.completedCalls}</span>
              <span class="mini-stat-label">Completed</span>
            </div>
          </div>
          <div class="mini-stat">
            <i class="fa-sharp fa-light fa-circle-xmark mini-stat-icon error"></i>
            <div class="mini-stat-info">
              <span class="mini-stat-value">${c.failedCalls}</span>
              <span class="mini-stat-label">Failed</span>
            </div>
          </div>
          <div class="mini-stat">
            <i class="fa-sharp fa-light fa-voicemail mini-stat-icon warning"></i>
            <div class="mini-stat-info">
              <span class="mini-stat-value">${c.voicemailCalls}</span>
              <span class="mini-stat-label">Voicemail</span>
            </div>
          </div>
          <div class="mini-stat">
            <i class="fa-sharp fa-light fa-hourglass-half mini-stat-icon neutral"></i>
            <div class="mini-stat-info">
              <span class="mini-stat-value">${remaining >= 0 ? remaining : 0}</span>
              <span class="mini-stat-label">Remaining</span>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="campaign-card-footer">
          <div class="campaign-meta">
            <span class="campaign-meta-item">
              <i class="fa-sharp fa-light fa-robot"></i>
              ${c.agent}
            </span>
            <span class="campaign-meta-item">
              <i class="fa-sharp fa-light fa-calendar"></i>
              ${c.createdAt}
            </span>
          </div>
          <div class="campaign-actions">
            ${c.status === 'active' ? html`
              <button class="action-btn" title="Pause" @click=${(e) => { e.stopPropagation(); }}>
                <i class="fa-sharp fa-light fa-pause"></i>
              </button>
            ` : c.status === 'paused' ? html`
              <button class="action-btn" title="Resume" @click=${(e) => { e.stopPropagation(); }}>
                <i class="fa-sharp fa-light fa-play"></i>
              </button>
            ` : ''}
            ${c.status !== 'draft' && c.status !== 'completed' ? html`
              <button class="action-btn danger" title="Stop" @click=${(e) => { e.stopPropagation(); }}>
                <i class="fa-sharp fa-light fa-stop"></i>
              </button>
            ` : ''}
            <button class="action-btn" title="View Details" @click=${() => this._selectCampaign(c)}>
              <i class="fa-sharp fa-light fa-arrow-right"></i>
            </button>
          </div>
        </div>
      </div>
    `;
  }

  // ─── Detail View ───
  _renderDetailView() {
    const c = this.selectedCampaign;
    const pct = this._getProgressPercent(c);
    return html`
      <div class="detail-view">
        <button class="back-btn" @click=${this._backToList}>
          <i class="fa-sharp fa-light fa-arrow-left"></i>
          Back to Campaigns
        </button>

        <!-- Header -->
        <div class="detail-header">
          <div class="detail-header-top">
            <div class="detail-title-row">
              <h1 class="detail-title">${c.name}</h1>
              ${this._getStatusBadge(c.status)}
            </div>
          </div>
          <div class="detail-progress progress-section">
            <div class="progress-bar-track">
              <div
                class="progress-bar-fill ${c.status === 'completed' ? 'complete' : ''}"
                style="width: ${pct}%"
              ></div>
            </div>
            <span class="progress-count">${pct}% — ${c.completedCalls} / ${c.totalCalls} calls</span>
          </div>
        </div>

        <!-- Tab Bar -->
        <div class="tab-bar">
          <div
            class="tab-item ${this.activeTab === 'results' ? 'active' : ''}"
            @click=${() => this._setTab('results')}
          >
            <i class="fa-sharp fa-light fa-chart-bar"></i>
            Results
          </div>
          <div
            class="tab-item ${this.activeTab === 'settings' ? 'active' : ''}"
            @click=${() => this._setTab('settings')}
          >
            <i class="fa-sharp fa-light fa-gear"></i>
            Settings
          </div>
          <div
            class="tab-item ${this.activeTab === 'contacts' ? 'active' : ''}"
            @click=${() => this._setTab('contacts')}
          >
            <i class="fa-sharp fa-light fa-address-book"></i>
            Contact List
          </div>
        </div>

        <!-- Tab Content -->
        ${this.activeTab === 'results' ? this._renderResultsTab(c) : ''}
        ${this.activeTab === 'settings' ? this._renderSettingsTab(c) : ''}
        ${this.activeTab === 'contacts' ? this._renderContactsTab(c) : ''}
      </div>
    `;
  }

  _renderResultsTab(c) {
    const successRate = c.completedCalls > 0
      ? Math.round(((c.completedCalls - c.failedCalls) / c.completedCalls) * 100)
      : 0;
    const vmRate = c.totalCalls > 0
      ? Math.round((c.voicemailCalls / c.totalCalls) * 100)
      : 0;

    return html`
      <!-- KPI Cards -->
      <div class="kpi-grid">
        <div class="card kpi-card">
          <div class="kpi-icon primary">
            <i class="fa-sharp-duotone fa-solid fa-phone"></i>
          </div>
          <div class="metric-label">Total Calls</div>
          <div class="metric-value">${c.completedCalls + c.failedCalls + c.voicemailCalls}</div>
        </div>
        <div class="card kpi-card">
          <div class="kpi-icon success">
            <i class="fa-sharp-duotone fa-solid fa-chart-line-up"></i>
          </div>
          <div class="metric-label">Success Rate</div>
          <div class="metric-value">${successRate}%</div>
        </div>
        <div class="card kpi-card">
          <div class="kpi-icon accent">
            <i class="fa-sharp-duotone fa-solid fa-timer"></i>
          </div>
          <div class="metric-label">Avg Duration</div>
          <div class="metric-value">2:24</div>
        </div>
        <div class="card kpi-card">
          <div class="kpi-icon warning">
            <i class="fa-sharp-duotone fa-solid fa-voicemail"></i>
          </div>
          <div class="metric-label">Voicemail Rate</div>
          <div class="metric-value">${vmRate}%</div>
        </div>
      </div>

      <!-- Results Table -->
      ${c.results.length > 0 ? html`
        <div class="table-container">
          <table class="data-table">
            <thead>
              <tr>
                <th>Phone Number</th>
                <th>Status</th>
                <th>Duration</th>
                <th>Outcome</th>
                <th>Sentiment</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              ${c.results.map(r => html`
                <tr>
                  <td style="font-family: var(--font-mono); font-size: var(--text-sm);">${r.phone}</td>
                  <td>
                    <div class="status-cell">
                      <span class="status-dot ${r.status === 'completed' ? 'active' : r.status === 'failed' ? 'error' : 'warning'}"></span>
                      ${r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                    </div>
                  </td>
                  <td style="font-family: var(--font-mono);">${r.duration}</td>
                  <td>${r.outcome}</td>
                  <td>
                    <span class="sentiment-${r.sentiment}">
                      <i class="fa-sharp fa-light ${r.sentiment === 'positive' ? 'fa-face-smile' : r.sentiment === 'negative' ? 'fa-face-frown' : 'fa-face-meh'}"></i>
                      ${r.sentiment.charAt(0).toUpperCase() + r.sentiment.slice(1)}
                    </span>
                  </td>
                  <td style="font-family: var(--font-mono); font-size: var(--text-xs); color: var(--color-text-tertiary);">${r.time}</td>
                </tr>
              `)}
            </tbody>
          </table>
        </div>
      ` : html`
        <div class="empty-state">
          <div class="empty-state-icon">
            <i class="fa-sharp fa-light fa-chart-bar"></i>
          </div>
          <div class="empty-state-title">No results yet</div>
          <div class="empty-state-description">Results will appear here once the campaign starts running.</div>
        </div>
      `}
    `;
  }

  _renderSettingsTab(c) {
    const s = c.settings;
    return html`
      <div class="settings-grid">
        <div class="card setting-group">
          <div class="setting-group-title">
            <i class="fa-sharp fa-light fa-robot"></i>
            Agent & Schedule
          </div>
          <div class="setting-row">
            <span class="setting-label">Agent</span>
            <span class="setting-value">${c.agent}</span>
          </div>
          <div class="setting-row">
            <span class="setting-label">Schedule</span>
            <span class="setting-value">${s.schedule}</span>
          </div>
          <div class="setting-row">
            <span class="setting-label">Caller ID</span>
            <span class="setting-value">${s.callerId}</span>
          </div>
          <div class="setting-row">
            <span class="setting-label">Max Call Duration</span>
            <span class="setting-value">${s.maxCallDuration}</span>
          </div>
        </div>

        <div class="card setting-group">
          <div class="setting-group-title">
            <i class="fa-sharp fa-light fa-arrows-rotate"></i>
            Retry & Concurrency
          </div>
          <div class="setting-row">
            <span class="setting-label">Max Retries</span>
            <span class="setting-value">${s.maxRetries}</span>
          </div>
          <div class="setting-row">
            <span class="setting-label">Retry Delay</span>
            <span class="setting-value">${s.retryDelay}</span>
          </div>
          <div class="setting-row">
            <span class="setting-label">Concurrency</span>
            <span class="setting-value">${s.concurrency} simultaneous</span>
          </div>
          <div class="setting-row">
            <span class="setting-label">Voicemail Detection</span>
            <span class="setting-value">
              <span class="toggle ${s.voicemailDetection ? 'active' : ''}"></span>
            </span>
          </div>
        </div>
      </div>
    `;
  }

  _renderContactsTab(c) {
    return html`
      <div class="contacts-summary">
        <div class="contacts-summary-stat">
          <span class="value">${c.contacts.total}</span>
          <span class="label">Total Contacts</span>
        </div>
        <div class="contacts-divider"></div>
        <div class="contacts-summary-stat">
          <span class="value">${c.contacts.valid}</span>
          <span class="label">Valid Numbers</span>
        </div>
        <div class="contacts-divider"></div>
        <div class="contacts-summary-stat">
          <span class="value">${c.contacts.invalid}</span>
          <span class="label">Invalid</span>
        </div>
      </div>

      <div class="table-container">
        <table class="data-table">
          <thead>
            <tr>
              <th>Phone Number</th>
              <th>Status</th>
              <th>Attempts</th>
              <th>Last Attempt</th>
              <th>Result</th>
            </tr>
          </thead>
          <tbody>
            ${(c.results || []).slice(0, 5).map((r, i) => html`
              <tr>
                <td style="font-family: var(--font-mono);">${r.phone}</td>
                <td>
                  <span class="badge ${r.status === 'completed' ? 'badge-success' : r.status === 'failed' ? 'badge-error' : 'badge-warning'}">
                    ${r.status.charAt(0).toUpperCase() + r.status.slice(1)}
                  </span>
                </td>
                <td style="font-family: var(--font-mono);">${i < 2 ? '1' : '2'}</td>
                <td style="font-family: var(--font-mono); font-size: var(--text-xs); color: var(--color-text-tertiary);">${r.time}</td>
                <td>${r.outcome}</td>
              </tr>
            `)}
          </tbody>
        </table>
      </div>
    `;
  }

  render() {
    return html`
      ${iconStyles}
      ${this.selectedCampaign
        ? this._renderDetailView()
        : this._renderCampaignList()}
    `;
  }
}

customElements.define('voice-campaigns', VoiceCampaigns);
